import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { LinkItem } from "@models/link.model"
import { api, AuthService } from "../services/authService"
import {
	BASE_LEGACY_URL,
	CREATE_ACCOUNT_LEGACY_APP_ID,
	FORGOT_PASSWORD_LEGACY_APP_ID,
	SSO_PARAM,
	TOKEN_PARAM,
	APP_ID,
	TOKEN_ID_PARAM,
	APP_ID_PARAM,
	PURCHASE_GIFT_ROYAL_TOOLCODE,
	IS_LOWER_EN_ENV,
	VITE_LOWER_ENV_USERNAME,
	VITE_LOWER_ENV_PASSWORD,
	Brand
} from "../utils/constants.util"
import { useAppShellContext } from "../context/AppShellProvider"
import { StorageKeys } from "@utils/user-config"
import secureLocalStorage from "react-secure-storage"
import { gzip } from "pako"
import { useUserStore } from "./useUserStore"

const santitizeAppId = (appId: string | string[] | undefined) =>
	typeof appId === "string" || !appId
		? appId
		: appId[0] && appId[0].split("/").length > 1
			? appId[0].split("/")[1]
			: undefined

export const useRedirect = () => {
	const navigate = useNavigate()
	const { closeMenu, openMenu } = useAppShellContext()
	const { userProfile } = useUserStore()

	const handleRedirect = useCallback(
		async ({ link, type, appId, params, retainBasePath, target }: LinkItem) => {
			if (!link || !type) {
				return console.error("Unknown link or type")
			}

			const menuHashTarget = link.endsWith("#global-nav")

			if (!menuHashTarget) {
				closeMenu?.()
			}
			switch (type) {
				case "internal-blank": {
					const internalUrl = await buildInternalRedirectUrl(link, params)
					if (internalUrl) window.open(internalUrl, "_blank")
					break
				}
				case "internal": {
					const internalUrl = await buildInternalRedirectUrl(link, params)
					secureLocalStorage.setItem(
						StorageKeys.HAS_USER_NAVIGATED_TO_LEGACY,
						"true"
					)
					if (internalUrl) window.location.href = internalUrl
					break
				}
				case "external": {
					if (appId === APP_ID.HYBRIS) {
						const primaryCurrency =
							userProfile?.currencies?.find(
								(currency) => currency.primaryCurrency
							)?.currencyCode || ""
						if (!params) {
							params = new Map<string, string>()
						}
						params.set("cur", primaryCurrency)
					}
					const sanitizedAppId = santitizeAppId(appId)
					const externalUrl = await buildExternalRedirectUrl(
						link,
						sanitizedAppId,
						params
					)
					if (externalUrl) {
						if (target && target === "current") {
							window.location.href = externalUrl.toString()
							return
						}

						window.open(externalUrl, "_blank")
					}
					break
				}
				case "external-jwt": {
					const externalJwtUrl = await buildExternalJwtRedirectUrl(link)
					if (externalJwtUrl) window.open(externalJwtUrl, "_blank")
					break
				}
				case "route":
					retainBasePath ? navigate(`.${link}`) : navigate(link)
					if (menuHashTarget) {
						requestAnimationFrame(() => {
							openMenu?.()
						})
					}
					break
				case "public":
					if (
						appId === CREATE_ACCOUNT_LEGACY_APP_ID ||
						appId === FORGOT_PASSWORD_LEGACY_APP_ID
					) {
						if (/^https?:\/\//i.test(link)) window.open(link, "_self")
						break
					}
					if (appId === PURCHASE_GIFT_ROYAL_TOOLCODE) {
						//for lower environment, when user clicks on royal purchase gift, we need to redirect to the gift using a post request and adding user and password in the header oppening it in new tab
						let headers: Record<string, string>
						if (IS_LOWER_EN_ENV) {
							headers = {
								"Content-Type": "application/json",
								username: VITE_LOWER_ENV_USERNAME ?? "",
								password: VITE_LOWER_ENV_PASSWORD ?? ""
							}
						} else {
							headers = {
								"Content-Type": "application/json"
							}
						}

						if (/^https?:\/\//i.test(link)) {
							const response = await fetch(link, {
								method: "POST",
								headers: headers
							})
							// Validate the final response URL is from the same origin as the
							// original link to prevent open redirect via server-side redirect
							try {
								const allowedOrigin = new URL(link).origin
								const finalOrigin = new URL(response.url).origin
								const targetUrl =
									finalOrigin === allowedOrigin ? response.url : link
								if (/^https?:\/\//i.test(targetUrl))
									window.open(targetUrl, "_blank")
							} catch {
								window.open(link, "_blank")
							}
						}
						break
					}
					if (/^https?:\/\//i.test(link)) window.open(link, "_blank")
					break
				default:
					console.error("Unknown link type")
			}
		},
		[navigate, closeMenu, openMenu]
	)

	const buildInternalRedirectUrl = async (
		target: string,
		params?: Map<string, string>
	) => {
		try {
			const token = AuthService.getAuthToken()
			if (!token) {
				return null
			}

			const remoteToken = await AuthService.remoteCPAccess(target)

			if (!remoteToken.remote_token) {
				return null
			}

			const urlObj = new URL(BASE_LEGACY_URL)
			const allParams: string[] = []
			allParams.push(`token=${encodeURIComponent(remoteToken.remote_token)}`)
			if (params) {
				params.forEach((value, key) =>
					allParams.push(
						`${encodeURIComponent(key)}=${encodeURIComponent(value)}`
					)
				)
			}
			const compressedParams = gzip(allParams.join("&"))
			const base64Encoded = btoa(
				Array.from(compressedParams)
					.map((c) => String.fromCharCode(c as number))
					.join("")
			)
			urlObj.searchParams.set("params", base64Encoded)
			return urlObj.toString()
		} catch (_error) {
			return null
		}
	}

	const buildExternalRedirectUrl = async (
		link: string,
		appId?: string,
		params?: Map<string, string>
	) => {
		const agencyId = userProfile?.agencyId
		const agentId = userProfile?.agentId

		const urlObj = new URL(link)
		if (
			link.includes("agency_id") &&
			link.includes("tagent_id") &&
			agencyId &&
			agentId
		) {
			urlObj.searchParams.set("agency_id", agencyId.toString())
			urlObj.searchParams.set("tagent_id", agentId.toString())
			return urlObj.href
		}

		if (params) {
			params.forEach((value, key) => urlObj.searchParams.set(key, value))
		}

		try {
			if (!appId) {
				return null
			}

			const externalToken = await AuthService.webserviceAccess(appId)
			if (!externalToken) {
				return null
			}

			if (appId !== APP_ID.IFRNDS) {
				urlObj.searchParams.append(APP_ID_PARAM, appId)
			}
			if (appId === APP_ID.EQUOTE || appId === APP_ID.VERB) {
				urlObj.searchParams.append(SSO_PARAM, externalToken.remoteAccessToken)
			} else if (appId === APP_ID.GMU) {
				urlObj.searchParams.append(
					TOKEN_ID_PARAM,
					externalToken.remoteAccessToken
				)
			} else {
				urlObj.searchParams.append(TOKEN_PARAM, externalToken.remoteAccessToken)
			}

			return urlObj.href
		} catch (_error) {
			return null
		}
	}

	const buildExternalJwtRedirectUrl = async (
		link: string
	): Promise<string | null> => {
		try {
			const { data } = await api.get<{ data: { token: string } }>(
				"/agents/signed-jwt/minimal"
			)
			//add the jwt to the link as a query parameter called jwt
			const urlObj = new URL(link)
			urlObj.searchParams.set("jwt", data?.data?.token)
			return urlObj.toString()
		} catch (_error) {
			return null
		}
	}

	const goToEspresso = ({
		bookingId,
		groupId
	}: {
		bookingId?: string
		groupId?: string
	}) => {
		const resType = groupId ? "GB" : "FIT"
		let json = ""
		if (bookingId) {
			json = JSON.stringify({
				reservationId: bookingId,
				reservationType: resType
			})
		} else {
			json = JSON.stringify({
				groupId,
				reservationType: resType
			})
		}
		handleRedirect({
			link: "espresso-search",
			type: "internal",
			params: new Map<string, string>([["predictiveSearchCriteriaJson", json]])
		})
	}

	const goToEspressoPayment = (bookingId: string, groupId: string) => {
		const resType = groupId ? "GB" : "FIT"
		const myMap = new Map()
		myMap.set("redirectToApp", "cpToPayment")
		myMap.set(
			"predictiveSearchCriteriaJson",
			JSON.stringify({
				reservationType: resType,
				reservationId: bookingId,
				groupId: groupId
			})
		)

		handleRedirect({
			link: "espresso-search",
			type: "internal",
			params: myMap
		})
	}

	const goToEspressoGapPoints = (groupId: string) => {
		const resType = "GS"
		const myMap = new Map()
		myMap.set("redirectToApp", "cpToGapRedemption")
		myMap.set(
			"predictiveSearchCriteriaJson",
			JSON.stringify({
				reservationType: resType,
				groupId: groupId
			})
		)

		handleRedirect({
			link: "espresso-search",
			type: "internal",
			params: myMap
		})
	}

	const handleViewAll = async (searchString: string) => {
		const customParams = {
			viewAll: "true",
			predictiveSearchString: searchString
		}
		const queryParams = JSON.stringify(customParams)
		handleRedirect({
			link: "espresso-search",
			type: "internal",
			params: new Map<string, string>([
				["predictiveSearchCriteriaJson", queryParams]
			])
		})
	}

	const handlePromoSearch = async (
		brand?: Brand,
		loyaltyNumber?: string,
		sailingDate?: string
	) => {
		const myMap = new Map()
		myMap.set("redirectToApp", "cpToTargetedOffers")
		myMap.set("brand", brand)
		myMap.set("loyaltyNumber", loyaltyNumber)
		myMap.set("sailDate", sailingDate)

		console.log(myMap)

		handleRedirect({
			link: "espresso-search",
			type: "internal",
			params: myMap
		})
	}

	return {
		handleRedirect,
		buildInternalRedirectUrl,
		goToEspresso,
		goToEspressoPayment,
		goToEspressoGapPoints,
		handleViewAll,
		handlePromoSearch
	}
}
